import GolfIcon from '@/components/GolfIcon';
import ChatList from '@/components/messages/ChatList';
import ChatViewContainer from '@/components/messages/ChatViewContainer';
import NewChatViewSupabase from '@/components/messages/NewChatViewSupabase';
import { useUserProfile } from '@/context/UserProfileContext';
import { Capacitor } from '@capacitor/core';
import { IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonPage, IonRedirect, IonRouterOutlet, IonSplitPane, IonTitle, IonToolbar } from '@ionic/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, useHistory, useParams } from 'react-router-dom';
import ChatListPage from './messages/ChatListPage';
import ChatViewPage from './messages/ChatViewPage';

export default function MessagesNew() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const [activeChat, setActiveChat] = useState<string | null>(conversationId || null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const { profile } = useUserProfile();
  const history = useHistory();
  const location = history.location;
  const { t } = useTranslation();

  // Update active chat when conversationId changes or when on base messages route
  useEffect(() => {
    // If we're on the base /messages route, reset active chat
    if (location.pathname === '/messages') {
      setActiveChat(null);
    } else {
      setActiveChat(conversationId || null);
    }
  }, [conversationId, location.pathname]);

  const handleSelectChat = (chatId: string) => {
    setActiveChat(chatId);

    if (Capacitor.isNativePlatform()) {
      history.push(`/messages/${chatId}`);
    }
  };

  const handleBackButton = () => {
    // If on native platform, let the hardware back button handle navigation
    if (Capacitor.isNativePlatform()) {
      history.goBack();
    } else {
      // On web, clear active chat and navigate to messages
      setActiveChat(null);
      history.push('/messages');
    }
  };

  const handleNewChat = () => {
    setShowNewChatModal(true);
  };

  if (!profile) {
    return (
      <IonRedirect to="/" />
    );
  }

  // On native platforms, use separate pages with navigation
  if (Capacitor.isNativePlatform()) {
    return (
      <IonPage>
        <IonContent>
          <IonRouterOutlet>
            <Route exact path="/messages" component={ChatListPage} />
            <Route path="/messages/:chatId" component={ChatViewPage} />
            <Redirect to="/messages" />
          </IonRouterOutlet>
        </IonContent>
      </IonPage>
    );
  }

  // On web, use the split view layout
  return (
    <IonPage>
      <IonSplitPane contentId="chat-main-content" className='bg-secondary' when="xs">
        {/* Chat List - Always visible on desktop, menu on mobile */}
        <div className={`md:w-2/5 md:m-3 w-full ${activeChat ? 'hidden md:block' : ''}`}>
          <IonHeader className='ion-no-border md:hidden'>
            <IonToolbar>
              <IonButtons slot="start">
                <IonBackButton text='' defaultHref="/dashboard" />
              </IonButtons>
              <IonTitle>{t('messages')}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={handleNewChat}>
                  <GolfIcon name="message-add" className="size-6 dark:invert" />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <ChatList
            className={`rounded-xl bg-card shadow `}
            userId={profile.userId}
            activeChat={activeChat}
            onSelectChat={handleSelectChat}
            onNewChat={handleNewChat}
          />
        </div>

        {/* Main Content Area */}
        <IonPage id="chat-main-content" className={`md:m-3 relative w-full ${activeChat ? '' : 'hidden md:block'}`}>
          {/* Chat View - Full screen on mobile, or when a chat is selected */}
          {activeChat ? (
            <ChatViewContainer
              className="rounded-xl bg-card shadow"
              userId={profile.userId}
              activeChat={activeChat}
              onBackButton={handleBackButton}
            />
          ) : (
            <div className="flex flex-col size-full items-center justify-center">
              <GolfIcon name="message-multiple" className="mx-auto size-20 opacity-70" />
              <h3 className="mt-2 text-lg font-medium">{t('select_chat_placeholder')}</h3>
            </div>
          )}
        </IonPage>

        {/* New Chat View */}
        {showNewChatModal && (
          <div className="absolute inset-0 z-10 bg-background">
            <NewChatViewSupabase
              closeNewChat={() => setShowNewChatModal(false)}
              setActiveChat={setActiveChat}
              userId={profile.userId}
            />
          </div>
        )}
      </IonSplitPane>
    </IonPage>
  );
}
